import { Spinner, SpinnerSize } from '@fluentui/react';
import React, { Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { lazyWithRetry } from 'src/utils/lazyWithRetry';
import { PilotUsersFlights, UATConstants } from '../../Common/Models/UATConstants';
import { UserContext } from '../../Models/UserProfile';
import Authorize, { TargetRedirection } from './Authorize';
import DataLoader from './DataLoader';
import { ITenant } from 'src/tenantConfig/interface';
import SharedQueries from '../../sharedqueries/SharedQueries';
// Lazy-loaded route components — split into separate chunks to reduce render-blocking JS
const HomeComponent = lazyWithRetry(() => import('../../dashboard/HomeComponent'));
const CreateActionForField = lazyWithRetry(() => import('../../dashboard/CreateActionForField'));
const CreateActionForCorp = lazyWithRetry(() => import('../../dashboard/CreateActionForCorp'));
const CreateSupportAction = lazyWithRetry(() => import('../../dashboard/CreateSupportAction'));
const CreateActionForAHPM = lazyWithRetry(() => import('../../dashboard/CreateActionForAHPM'));
const CreateActionAPEX = lazyWithRetry(() => import('../../dashboard/CreateActionAPEX'));
const WaveReport = lazyWithRetry(() => import('../../../src/dashboard/WaveReport'));
const DeprecatedTenants: any = lazyWithRetry(() => import('src/dashboard/DeprecatedTenants'));
const ActionDetailsWrapper = lazyWithRetry(() => import('src/actiondetails/ActionDetailsWrapper'));
const UserRatingPage = lazyWithRetry(() => import('../Pages/UserRatingPage'));
const ManageViewPage = lazyWithRetry(() => import('src/Common/manageViews/components/pages/ManageView'));
const ManageActionWrapper = lazyWithRetry(() => import('src/manageAction/ManageActionWrapper'));
const DirectFieldRequestWrapper = lazyWithRetry(() => import('src/dashboard/DirectFieldRequestWrapper'));

interface RouteState { }

interface RouteProps {
  previousPaths: string[];
  searchKeyword: string;
  post_logout_redirect_uri: string;
  user: UserContext;
  userAuthToken: string | undefined;
  showManageViews: boolean;
  handleManageViewClick: (data: boolean) => void;
  isNavCollapsed: boolean;
  toggleNavCollapsed: () => void;
  vacTenant?: ITenant[];
}

export default class Routes extends React.Component<RouteProps, RouteState> {
  constructor(props: RouteProps) {
    super(props);
    this.state = {};
  }

  private navigateToPreviousPath() {
    const previousPathProps = this.props.previousPaths;
    let previousPath = '/';
    if (previousPathProps && previousPathProps.length > 0) {
      if (previousPathProps[previousPathProps.length - 1] !== window.location.pathname) {
        previousPath = previousPathProps[previousPathProps.length - 1];
      } else if (previousPathProps.length > 1) {
        previousPath = previousPathProps[previousPathProps.length - 2];
      }
    }
    window.location.replace(previousPath);
  }

  componentWillUnmount() {
    // fix Warning: Can't perform a React state update on an unmounted component
    this.setState = () => {
      return;
    };
  }

  render() {
    return (
      <Switch>
        {[
          UATConstants.Routes.Overview,
          UATConstants.Routes.Index,
          UATConstants.Routes.IndexHtml,
          UATConstants.Routes.Home,
          UATConstants.Routes.LandingPage,
          UATConstants.Routes.TenantOverviewHub,
        ].map((path) => (
          <Route
            exact={true}
            key={1}
            path={path}
            render={() => {
              const tenantId = new URL(window.location.href).searchParams.get('tenantId');
              return (
                <Authorize
                  key={`authorize-${tenantId || 'no-tenant'}`} // Force re-mount when tenant changes
                  user={this.props.user}
                  vacTenant={this.props.vacTenant}
                  redirectTarget={TargetRedirection.ErrorPage}
                  title={UATConstants.PermissionMsgs.DontHavePermission}
                  message={UATConstants.PermissionMsgs.ContactAdminToGrantAccess}
                  requiredFlights={PilotUsersFlights}
                  checkAny={true}
                  isMainPage={true}
                  callBack={() => this.navigateToPreviousPath()}
                  isDialogVisible={true}
                  loadDropDown={false}>
                  <Suspense
                    fallback={
                      <Spinner
                        size={SpinnerSize.medium}
                        label={UATConstants.PlaceholdersAndTitles.Loading}
                        ariaLive='assertive'
                        className={'defaultSpinnerWidth'}
                      />
                    }>
                    <HomeComponent
                      userAuthToken={this.props.userAuthToken}
                      showManageViews={this.props.showManageViews}
                      handleManageViewClick={this.props.handleManageViewClick}
                      isNavCollapsed={this.props.isNavCollapsed}
                      toggleNavCollapsed={this.props.toggleNavCollapsed}
                      tenantId={new URL(window.location.href).searchParams.get('tenantId')}
                      isHubOverview={path.includes(UATConstants.Routes.TenantOverviewHub)}
                    />
                  </Suspense>
                </Authorize>
              );
            }}
          />
        ))}
        <Route
          exact={true}
          path={UATConstants.Routes.SharedQueriesPage}
          component={() => {
            return (
              <Authorize
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                title={UATConstants.PermissionMsgs.DontHavePermission}
                message={UATConstants.PermissionMsgs.ContactAdminToGrantAccess}
                requiredFlights={PilotUsersFlights}
                checkAny={true}
                isMainPage={true}
                callBack={() => this.navigateToPreviousPath()}
                isDialogVisible={true}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <div className='page-centered-spinner'>
                      <Spinner
                        size={SpinnerSize.medium}
                        label={UATConstants.PlaceholdersAndTitles.Loading}
                        ariaLive='assertive'
                      />
                    </div>
                  }>
                  <SharedQueries />
                </Suspense>
              </Authorize>
            );
          }}
        />

        <Route
          exact={true}
          path={UATConstants.Routes.SharedQueriesWithParam}
          component={() => {
            return (
              <Authorize
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                title={UATConstants.PermissionMsgs.DontHavePermission}
                message={UATConstants.PermissionMsgs.ContactAdminToGrantAccess}
                requiredFlights={PilotUsersFlights}
                checkAny={true}
                isMainPage={true}
                callBack={() => this.navigateToPreviousPath()}
                isDialogVisible={true}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <SharedQueries flights={this.props.user.flights} />
                </Suspense>
              </Authorize>
            );
          }}
        />

        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPage}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateActionForCorp
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />

        <Route
          exact={true}
          path={UATConstants.Routes.ManageView}
          component={() => {
            return (
              <Authorize
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                title={UATConstants.PermissionMsgs.DontHavePermission}
                message={UATConstants.PermissionMsgs.ContactAdminToGrantAccess}
                requiredFlights={PilotUsersFlights}
                checkAny={true}
                isMainPage={true}
                callBack={() => this.navigateToPreviousPath()}
                isDialogVisible={true}
                loadDropDown={false}>
                <Suspense
                  fallback={
                    <div className='page-centered-spinner'>
                        <Spinner
                        size={SpinnerSize.medium}
                        label={UATConstants.PlaceholdersAndTitles.Loading}
                        ariaLive='assertive'
                        />
                    </div>
                  }>
                  <ManageViewPage
                    apiTokens={{
                      authTokens: sessionStorage.getItem(UATConstants.jwtTokenForApi) || '',
                      graphToken: sessionStorage.getItem(UATConstants.jwtTokenForGraph) || '',
                      coreAPIToken: sessionStorage.getItem(UATConstants.jwtTokenForCoreApi) || '',
                    }}
                    user={this.props.user as any}
                  //isNavCollapsed={this.props.isNavCollapsed} //TODO: Need to check if this is required. Commented it out as part of Artemis onboarding
                  />
                </Suspense>
              </Authorize>
            );
          }}
        />

        <Route
          exact={true}
          path={UATConstants.Routes.UserRatingPage}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <div className='page-centered-spinner'>
                      <Spinner
                        size={SpinnerSize.medium}
                        label={UATConstants.PlaceholdersAndTitles.Loading}
                        ariaLive='assertive'
                      />
                   </div>
                  }>
                  <UserRatingPage></UserRatingPage>
                </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForCorp}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateActionForCorp
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForMacc}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateActionForCorp
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForMWHubCustomer}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <DeprecatedTenants
                    isCreate={true}
                    isSplitView={false}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />

        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForAHPM}
          component={() => {
            return (
              <Authorize
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                title={UATConstants.PermissionMsgs.DontHavePermission}
                message={UATConstants.PermissionMsgs.ContactAdminToGrantAccess}
                requiredFlights={PilotUsersFlights}
                checkAny={true}
                isMainPage={true}
                callBack={() => this.navigateToPreviousPath()}
                isDialogVisible={true}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateActionForAHPM
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </Authorize>
            );
          }}
        />

        <Route
          exact={true}
          path={UATConstants.Routes.CreateReport}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <WaveReport
                    isToOpenPanel={false}
                    isPanelExpanded={false}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />

        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForField}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                  <Suspense
                    fallback={
                      <div className='page-centered-spinner'>
                        <Spinner
                          size={SpinnerSize.medium}
                          label={UATConstants.PlaceholdersAndTitles.Loading}
                          ariaLive='assertive'
                        />
                      </div>
                    }>
                    <DirectFieldRequestWrapper>
                        <CreateActionForField
                          isToOpenPanel={false}
                          isPanelExpanded={true}
                          dismissActionPanel={() => { }}
                          toggleScreen={() => { }}
                        />
                    </DirectFieldRequestWrapper>
                  </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForIat}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateActionForField
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForSupportScrum}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateSupportAction
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionPageForSupport}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateSupportAction
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          exact={true}
          path={UATConstants.Routes.CreateActionAPEX}
          component={() => {
            return (
              <DataLoader
                user={this.props.user}
                redirectTarget={TargetRedirection.ErrorPage}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                  }>
                  <CreateActionAPEX
                    isToOpenPanel={false}
                    isPanelExpanded={true}
                    dismissActionPanel={() => { }}
                    toggleScreen={() => { }}
                  />
                </Suspense>
              </DataLoader>
            );
          }}
        />
        <Route
          path={UATConstants.Routes.ManageActionPage}
          component={() => {
            return (
              <Authorize
                user={this.props.user}
                requiredFlights={PilotUsersFlights}
                redirectTarget={TargetRedirection.ErrorPage}
                title={UATConstants.PermissionMsgs.DontHavePermission}
                message={UATConstants.PermissionMsgs.ContactAdminToGrantAccess}
                requiredPermissions={[]}
                checkAny={true}
                isMainPage={true}
                callBack={() => this.navigateToPreviousPath()}
                isDialogVisible={true}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <div className='page-centered-spinner'>
                    <Spinner
                      size={SpinnerSize.medium}
                      label={UATConstants.PlaceholdersAndTitles.Loading}
                      ariaLive='assertive'
                    />
                    </div>
                  }>
                  <ActionDetailsWrapper authToken={this.props.userAuthToken as string} />
                </Suspense>
              </Authorize>
            );
          }}
        />
        {[UATConstants.Routes.Dashboard, UATConstants.Routes.DashboardSummary].map((path) => (
          <Route exact={true} key={path} path={path}>
            <Redirect to={UATConstants.Routes.Overview} />
          </Route>
        ))}

        <Route
          path={UATConstants.Routes.ManageAction}
          component={() => {
            return (
              <Authorize
                user={this.props.user}
                requiredFlights={PilotUsersFlights}
                redirectTarget={TargetRedirection.ErrorPage}
                title={UATConstants.PermissionMsgs.DontHavePermission}
                message={UATConstants.PermissionMsgs.ContactAdminToGrantAccess}
                requiredPermissions={[]}
                checkAny={true}
                isMainPage={true}
                callBack={() => this.navigateToPreviousPath()}
                isDialogVisible={true}
                loadDropDown={true}>
                <Suspense
                  fallback={
                    <div className='page-centered-spinner'>
                        <Spinner
                          size={SpinnerSize.medium}
                          label={UATConstants.PlaceholdersAndTitles.Loading}
                          ariaLive='assertive'
                        />
                    </div>
                  }>
                  <ManageActionWrapper
                    user={this.props.user as any}
                    authToken={this.props.userAuthToken as string}
                  />
                </Suspense>
              </Authorize>
            );
          }}
        />
      </Switch>
    );
  }
}

// Generated by GitHub Copilot | Updated on: 2026-04-09 | 5 lines modified | Wrapped createaction/field route with DirectFieldRequestWrapper for MSX info screen
// Generated by GitHub Copilot | Updated on: 2026-05-15 | 18 lines modified | Converted eager imports to lazy-loaded chunks to reduce render-blocking JS
// Generated by GitHub Copilot | Updated on: 2026-09-08 | 34 lines modified | Redirected My Dashboard routes to All
